import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { Moment } from 'moment';
import { FocusedInputShape } from 'react-dates';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { Text } from 'components/ui/forms';
import {
  Button,
  DatePickerRange,
  DatePickerRangeProps,
} from 'components/ui/general';
import { TableProps } from 'components/ui/table';
import { DateFormat } from 'consts/date';
import { useClickOutside, useKeyPress, useTable } from 'hooks';
import { format } from 'utils';

import { texts } from './FilterDateRange.text';

import styles from './FilterDateRange.module.scss';

export type FilterDateRangeProps = {
  dateRange: {
    nameFrom: string;
    nameTo: string;
    placeholder: string;
    datePicker?: Partial<DatePickerRangeProps>;
  };
  handleQueryParameters?: TableProps['handleQueryParameters'];
  onFilterChange: (date: Moment | null, name: string) => void;
};

export const FilterDateRange = ({
  dateRange,
  handleQueryParameters,
  onFilterChange,
}: FilterDateRangeProps) => {
  const { getFilterQueryParameter } = useTable();
  const ref = useRef<HTMLDivElement>(null);
  const defaultValues = useMemo(() => {
    const collectDefaultValues: {
      startDate: Moment | null;
      endDate: Moment | null;
    } = {
      startDate: null,
      endDate: null,
    };
    const getFilter = getFilterQueryParameter();

    if (!getFilter?.length || !handleQueryParameters) {
      return collectDefaultValues;
    }

    getFilter.forEach((filter: any) => {
      const [getName, getDate] = filter.split('__');

      if (getName === dateRange.nameFrom) {
        collectDefaultValues.startDate = format.date({ date: getDate });
      }

      if (getName === dateRange.nameTo) {
        collectDefaultValues.endDate = format.date({ date: getDate });
      }
    });

    return collectDefaultValues;
  }, [
    dateRange.nameFrom,
    dateRange.nameTo,
    getFilterQueryParameter,
    handleQueryParameters,
  ]);
  const [startDate, setStartDate] = useState<Moment | null>(
    defaultValues.startDate
  );
  const [endDate, setEndDate] = useState<Moment | null>(defaultValues.endDate);
  const [focusedInput, setFocusedInput] =
    useState<FocusedInputShape>('startDate');
  const { register, setValue } = useForm<any>();
  const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);

  useEffect(() => {
    setStartDate(defaultValues.startDate);
    setEndDate(defaultValues.endDate);
  }, [defaultValues]);

  useClickOutside(ref, () => {
    setDatePickerVisible(false);
  });

  useKeyPress(27, () => {
    setDatePickerVisible(false);
  });

  useEffect(() => {
    const from = startDate ? startDate.format(DateFormat.DateStamp) : '';
    const to = endDate ? endDate.format(DateFormat.DateStamp) : '';

    setValue(dateRange.nameFrom, from && to ? `${from} – ${to}` : from);
  }, [startDate, endDate, setValue, dateRange.nameFrom]);

  const handleDatesChange = useCallback(
    ({
      startDate: startDateChange,
      endDate: endDateChange,
    }: {
      startDate: Moment | null;
      endDate: Moment | null;
    }) => {
      const nextStartDate = startDateChange
        ? format.date({ date: startDateChange })
        : null;
      const nextEndDate = endDateChange
        ? format.date({ date: endDateChange })
        : null;

      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
      onFilterChange(nextStartDate, dateRange.nameFrom);
      onFilterChange(nextEndDate, dateRange.nameTo);

      if (nextStartDate && nextEndDate) {
        setDatePickerVisible(false);
        setFocusedInput('startDate');
      }
    },
    [dateRange.nameFrom, dateRange.nameTo, onFilterChange]
  );

  const handleClear = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    onFilterChange(null, dateRange.nameFrom);
    onFilterChange(null, dateRange.nameTo);
    setFocusedInput('startDate');
    setDatePickerVisible(false);
  }, [dateRange.nameFrom, dateRange.nameTo, onFilterChange]);

  return (
    <div
      className={classNames(styles.root, {
        [styles.datePickerVisible]: datePickerVisible,
      })}
      ref={ref}
    >
      <button
        type="button"
        className={styles.textHolder}
        onClick={() => setDatePickerVisible(!datePickerVisible)}
      >
        <fieldset disabled className={styles.textFieldset}>
          <Text
            register={register}
            name={dateRange.nameFrom}
            fullWidth
            iconRight={{ name: 'calendar-day' }}
            placeholder={dateRange.placeholder}
          />
        </fieldset>
      </button>
      <div className={styles.datePicker}>
        <DatePickerRange
          startDate={startDate}
          endDate={endDate}
          onDatesChange={handleDatesChange}
          focusedInput={focusedInput}
          onFocusChange={(focusChange) =>
            setFocusedInput(focusChange || 'startDate')
          }
          initialVisibleMonth={() => startDate || format.date()}
          numberOfMonths={1}
          minimumNights={0}
          {...dateRange.datePicker}
        />
        {(!!startDate || !!endDate) && (
          <div className={styles.clearHolder}>
            <Button size="sm" color="secondary" ghost onClick={handleClear}>
              <FormattedMessage {...texts.clear} />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
